import React, { useState } from "react";
import styled from "styled-components";
import { createAnimation, animations } from "./animations";
import MobileQRScanner from "./MobileQRScanner";

// Add Google Fonts import
const fontLink = document.createElement("link");
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@100;200;300;400;500;600;700;800;900&display=swap";
fontLink.rel = "stylesheet";
document.head.appendChild(fontLink);

const MobileContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, #304da1, #41a4d9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  z-index: 1000;
  font-family: "Montserrat", sans-serif;
  overflow-y: auto;
  height: 100vh;
  height: -webkit-fill-available;
  padding: env(safe-area-inset-top) env(safe-area-inset-right)
    env(safe-area-inset-bottom) env(safe-area-inset-left);
  box-sizing: border-box;
`;

const InnerContainer = styled.div`
  width: 100%;
  max-width: 430px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: scale(0.85);
  transform-origin: top center;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 50px;
`;

const Logo = styled.div`
  width: 100%;
  padding: 2rem 0.73rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  box-sizing: border-box;
  margin: 0;
  z-index: 2;
  --logo-scale: 1.1; /* Base scale factor - adjust this to scale everything */
  --logo-width: calc(102px * var(--logo-scale));
  --logo-padding-vertical: calc(2.5rem * var(--logo-scale));
  --logo-padding-horizontal: calc(0.73rem * var(--logo-scale));
  --subtitle-font-size: calc(0.51rem * var(--logo-scale));
  --subtitle-width: calc(146px * var(--logo-scale));
  --subtitle-margin: calc(0.38rem * var(--logo-scale));
  --line-height: calc(1.46px * var(--logo-scale));
  --line-max-width: calc(29px * var(--logo-scale));
  --line-min-width: calc(14px * var(--logo-scale));
  --number-font-size: calc(0.87rem * var(--logo-scale));
  --number-margin: calc(0.37rem * var(--logo-scale));
  --logo-margin-bottom: calc(0.1rem * var(--logo-scale));

  img {
    width: var(--logo-width);
    margin-bottom: var(--logo-margin-bottom);
    filter: brightness(0) invert(1);
    position: relative;
    left: 2px;
  }

  .subtitle {
    font-size: var(--subtitle-font-size);
    text-transform: uppercase;
    letter-spacing: 1px;
    line-height: 1.5;
    font-weight: 700;
    color: white;
    text-align: center;
    width: var(--subtitle-width);
    margin: var(--subtitle-margin) auto 0;
    font-family: "Montserrat", sans-serif;
  }

  .number-container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0rem;
    color: white;
    width: 100%;
  }

  .line {
    height: var(--line-height);
    background: white;
    flex: 1;
    max-width: var(--line-max-width);
    min-width: var(--line-min-width);
    opacity: 0.78;
    display: block;
    position: relative;
    z-index: 1;
  }

  .number {
    margin: 0 var(--number-margin);
    font-size: var(--number-font-size);
    font-weight: 700;
    color: white;
    position: relative;
    z-index: 2;
    font-family: "Montserrat", sans-serif;
  }
`;

const ContentCard = styled.div`
  background: white;
  width: 100%;
  min-height: 75vh;
  margin: 0 auto;
  padding: 1.5rem;
  padding-bottom: 2rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
  opacity: 0;
  animation: ${createAnimation(animations.mobileContentCard)};
  margin-bottom: 2rem;
  position: relative;
  --corner-offset: 15.2px;
  mask-image: radial-gradient(
      circle at calc(-1 * var(--corner-offset)) calc(-1 * var(--corner-offset)),
      transparent 50px,
      white 50px
    ),
    radial-gradient(
      circle at calc(100% + var(--corner-offset))
        calc(-1 * var(--corner-offset)),
      transparent 50px,
      white 50px
    ),
    radial-gradient(
      circle at calc(-1 * var(--corner-offset))
        calc(100% + var(--corner-offset)),
      transparent 50px,
      white 50px
    ),
    radial-gradient(
      circle at calc(100% + var(--corner-offset))
        calc(100% + var(--corner-offset)),
      transparent 50px,
      white 50px
    );
  mask-size: 50% 50%;
  mask-position: 0 0, 100% 0, 0 100%, 100% 100%;
  mask-repeat: no-repeat;
  -webkit-mask-image: radial-gradient(
      circle at calc(-1 * var(--corner-offset)) calc(-1 * var(--corner-offset)),
      transparent 50px,
      white 50px
    ),
    radial-gradient(
      circle at calc(100% + var(--corner-offset))
        calc(-1 * var(--corner-offset)),
      transparent 50px,
      white 50px
    ),
    radial-gradient(
      circle at calc(-1 * var(--corner-offset))
        calc(100% + var(--corner-offset)),
      transparent 50px,
      white 50px
    ),
    radial-gradient(
      circle at calc(100% + var(--corner-offset))
        calc(100% + var(--corner-offset)),
      transparent 50px,
      white 50px
    );
  -webkit-mask-size: 50% 50%;
  -webkit-mask-position: 0 0, 100% 0, 0 100%, 100% 100%;
  -webkit-mask-repeat: no-repeat;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -50px;
    width: 100px;
    height: 50px;
    background: transparent;
    border-bottom-left-radius: 50px;
    border-bottom-right-radius: 50px;
    z-index: -1;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    right: -50px;
    width: 100px;
    height: 50px;
    background: transparent;
    border-top-left-radius: 50px;
    border-top-right-radius: 50px;
    z-index: -1;
  }

  .corner-top-right {
    position: absolute;
    top: 0;
    right: -50px;
    width: 100px;
    height: 50px;
    background: transparent;
    border-bottom-left-radius: 50px;
    border-bottom-right-radius: 50px;
    z-index: -1;
  }

  .corner-bottom-left {
    position: absolute;
    bottom: 0;
    left: -50px;
    width: 100px;
    height: 50px;
    background: transparent;
    border-top-left-radius: 50px;
    border-top-right-radius: 50px;
    z-index: -1;
  }
`;

const AuthenticIcon = styled.div`
  margin: 1.5rem 0 1.5rem;
  width: 152px;
  height: 152px;

  img {
    width: 100%;
    height: auto;
  }
`;

const VerificationText = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  font-family: "Montserrat", sans-serif;

  h2 {
    font-size: 1.4rem;
    color: #333;
    margin: 0 0 1rem;
    font-weight: 400;
    line-height: 1.3;
    font-family: "Montserrat", sans-serif;

    .highlight {
      color: #61c53a;
      font-weight: 900;
    }
  }

  .thank-you {
    color: #333;
    letter-spacing: 0rem;
    font-size: 1rem;
    margin-top: 1.26rem;
    margin-bottom: -1.26rem;
    opacity: 0.52;
    font-family: "Montserrat", sans-serif;
  }
`;

const ProductDetails = styled.div`
  width: 100%;
  margin: 0.65rem 0.65rem 0rem 4rem;
  align-items: center;
  padding: 0 1rem;
  line-height: 1;
  letter-spacing: 0.15rem;
  font-family: "Montserrat", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .detail-item {
    line-height: 1.26;
    margin-bottom: 0.3rem;
    text-align: left;
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    font-family: "Montserrat", sans-serif;
    width: 100%;

    &.product-label {
      font-size: 1.2rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 0.3rem;
    }

    &.product-name {
      font-size: 1.2rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 0.3rem;
    }

    &.info {
      margin-top: 0.3rem;
      font-size: 1rem;
      color: #333;
      line-height: 1;
      font-weight: 500;
    }
  }
`;

const WebsiteButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #3b5aa9;
  color: white;
  border-radius: 50px;
  padding: 0.9rem 1.4rem;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 600;
  margin: 2rem 0;
  gap: 0.5rem;
  position: relative;
  min-width: 6rem;
  z-index: 1001;
  text-align: center;
  font-family: "Montserrat", sans-serif;

  img {
    width: 18px;
    height: 18px;
    filter: brightness(0) invert(1);
    padding-left: 0.5rem;
    border-left: 2px solid rgba(255, 255, 255, 0.8);
  }
`;

const UKEntity = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 0;
  right: 0;
  text-align: center;
  color: #3e58a3;
  font-size: 0.9rem;
  margin: 0rem 0 1rem;
  position: relative;
  letter-spacing: 0.08rem;
  z-index: 2;
  width: 100%;
  opacity: 0.7;
  font-family: "Montserrat", sans-serif;
`;

const QRCodeImage = styled.img`
  width: 40px;
  height: 40px;
  margin: 0rem 0;
  display: block;
`;

const MaterialIconLink = styled.a`
  position: fixed;
  bottom: 20px;
  right: 20px;
  color: #304da1;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  z-index: 9999;
  cursor: pointer;

  .material-symbols-outlined {
    font-size: 1.5rem;
    transition: color 0.3s ease;
    font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48;
  }

  &:hover {
    .material-symbols-outlined {
      color: #41a4d9;
    }
  }
`;

const MobileValidProduct = ({ verificationResult }) => {
  const [showContent, setShowContent] = useState(false);
  const [qrPositioned, setQrPositioned] = useState(false);
  const [qrGrowing, setQrGrowing] = useState(false);

  const handleQRAnimationComplete = () => {
    setQrPositioned(true);
    setShowContent(true);
    setTimeout(() => {
      setQrGrowing(true);
    }, 500);
  };

  const handleWebsiteClick = (e) => {
    e.preventDefault();
    window.location.href = "https://barjuicevape.com";
  };

  // Format icon name for Material Icons
  const formatIconName = (name) => {
    if (!name) return "info";
    return name.replace(/\s+/g, "_");
  };

  const productName =
    verificationResult.data.productBrand && verificationResult.data.productName
      ? `${verificationResult.data.productBrand} ${verificationResult.data.productName}`
      : verificationResult.data.productName || "Bar Juice Product";
  const firstScanned = verificationResult.data.firstScanned
    ? new Date(verificationResult.data.firstScanned).toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }
      )
    : new Date().toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
  const scanCount = verificationResult.data.scanCount;
  const code = verificationResult.data.code || "xxxxxxxxxxxxx";

  return (
    <MobileContainer>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200"
      />
      {process.env.REACT_APP_MATERIAL_ICON_HIDE !== "true" && (
        <MaterialIconLink
          href={`${
            process.env.REACT_APP_MATERIAL_ICON_URL || "https://example.com"
          }${verificationResult?.code || "test"}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="material-symbols-outlined">
            {formatIconName(process.env.REACT_APP_MATERIAL_ICON_NAME)}
          </span>
        </MaterialIconLink>
      )}
      <InnerContainer>
        <Logo>
          <img src="/images/BJ-logo.png" alt="Bar Juice" />
          <div className="number-container">
            <div className="line" style={{ backgroundColor: "white" }} />
            <div className="number">5000</div>
            <div className="line" style={{ backgroundColor: "white" }} />
          </div>
          <div className="subtitle">REFILLABLE E-LIQUID</div>
        </Logo>

        {showContent && (
          <ContentCard>
            <div className="corner-top-right" />
            <div className="corner-bottom-left" />
            <AuthenticIcon>
              <img src="/images/authentic-2.png" alt="Authentic Product" />
            </AuthenticIcon>

            <VerificationText>
              <h2>
                Your product is <br />
                <span className="highlight">authentic</span> and has passed
                <br />
                our verification system.
              </h2>
              <div className="thank-you">Thank you for choosing Bar Juice.</div>
            </VerificationText>

            <ProductDetails>
              <div className="detail-item product-label">Product:</div>
              <div className="detail-item product-name">{productName}</div>
              <div className="detail-item info">
                First Scanned: {firstScanned}
              </div>
              <div className="detail-item info">
                Number of Scans: {scanCount}
              </div>
              <div className="detail-item info">Code: {code}</div>
            </ProductDetails>

            <WebsiteButton onClick={handleWebsiteClick}>
              go to barjuicevape.com
              <img src="/icons/icon-network.svg" alt="Globe" />
            </WebsiteButton>

            {process.env.REACT_APP_UK_RESPONSIBLE_ENTITY === "true" &&
              verificationResult.data.customerId && (
                <UKEntity>
                  UK Responsible Entity:
                  <br />
                  {verificationResult.data.customerId}
                </UKEntity>
              )}
          </ContentCard>
        )}

        {showContent && (
          <QRCodeImage src="/images/dummy-qr.png" alt="QR Code" />
        )}
      </InnerContainer>

      <MobileQRScanner
        onAnimationComplete={handleQRAnimationComplete}
        className={`${qrPositioned ? "move-to-position" : ""} ${
          qrGrowing ? "grow-and-blend" : ""
        }`}
        isNetworkError={false}
      />
    </MobileContainer>
  );
};

export default MobileValidProduct;
